import {
  HMIKeyType,
  HMIParamsType,
  IHMI,
  IHMIParamsResponse,
  ISetHMIParams,
} from "@/core/models/hmi.model";
import {
  IPredictionRequest,
  IPredictionResponse,
} from "@/core/models/prediction.model";
import {
  IRecommendationOutput,
  IRecommendationPayload,
  IRecommendationRequest,
  IRecommendationResponse,
  IRecommendationResultParameter,
  RecommendationParamEnum,
} from "@/core/models/recommendation.model";
import { fixNumber } from "@/core/utils/decoder.utils";

export const HMIParamsTransformer = (
  response: IHMIParamsResponse[],
  { type, data: paramsData, checkEnable }: ISetHMIParams
): IHMI["params"] => {
  return response.reduce((acc, item) => {
    const {
      id,
      name,
      minimum,
      maximum,
      default: defaultValue,
      last_value: latestValue,
      input: enabled,
    } = item;
    const calcValue = paramsData
      ? fixNumber(paramsData[name])
      : type === "default"
      ? fixNumber(defaultValue)
      : type === "latest"
      ? fixNumber(latestValue)
      : null;
    const value = checkEnable ? (enabled ? calcValue : null) : calcValue;
    acc[name] = {
      id,
      value,
      min: minimum,
      max: maximum,
      enable: enabled,
      error: Number(value) < minimum || Number(value) > maximum,
    };
    return acc;
  }, {} as HMIParamsType);
};

export const predictionPayloadTransformer = (
  data: HMIParamsType,
  interval: number
): IPredictionRequest => ({
  interval,
  predictive_parameters: Object.values(data).map((param) => ({
    parameter: param.id,
    value: Number(param.value),
  })),
});

export const predictionResultTransformer = (
  response: IPredictionResponse
): IHMI["predictionResult"] => ({
  id: response.id,
  carbon: response.carbon,
  metallization: response.metallization,
  productionRate: response.production_rate,
});

export const predictionHistoryTransformer = (
  response: IPredictionResponse[]
): IHMI["predictionHistory"] => {
  return response.map((prediction) => {
    let inputParameters: Partial<Record<HMIKeyType, number>> = {};
    prediction.input_parameters.forEach((param) => {
      inputParameters = {
        ...inputParameters,
        [param.parameter.name]: param.value,
      };
    });

    return {
      id: prediction.id,
      interval: prediction.interval,
      carbon: prediction.carbon,
      metallization: prediction.metallization,
      productionRate: prediction.production_rate,
      time: new Date(prediction.created_at),
      inputParameters: inputParameters as Record<HMIKeyType, number>,
    };
  });
};

export const recommendationPayloadTransformer = (
  params: HMIParamsType,
  data: IRecommendationPayload
): IRecommendationRequest => {
  const filteredParams = Object.values(params).filter((param) => param.enable);
  return {
    interval: data.interval,
    target: RecommendationParamEnum[data.target],
    target_value: data.value,
    predictive_parameters: filteredParams.map((param) => ({
      parameter: param.id,
      value: Number(param.value),
    })),
  };
};

const recommendationOutputsTransformer = (
  response: IRecommendationOutput[]
): IRecommendationResultParameter[] => {
  let filteredParams: Partial<Record<HMIKeyType, number>> = {};
  response.forEach((param) => {
    const key = param.parameter.name;
    const value = param.value;
    const currentValue = filteredParams[key];
    if (currentValue === undefined || currentValue < value) {
      filteredParams = { ...filteredParams, [key]: value };
    }
  });
  return Object.entries(filteredParams).map(([key, value]) => ({
    name: key as HMIKeyType,
    value: value as number,
  }));
};

export const recommendationResultTransformer = (
  response: IRecommendationResponse
): IHMI["recommendationResult"] => ({
  id: response.id,
  carbon: response.carbon,
  metallization: response.metallization,
  productionRate: response.production_rate,
  parameters: recommendationOutputsTransformer(response.output),
});

export const recommendationHistoryTransformer = (
  response: IRecommendationResponse[]
): IHMI["recommendationHistory"] => {
  return response.map((recommendation) => {
    let inputParameters: Partial<Record<HMIKeyType, number>> = {};
    recommendation.input_parameters.forEach((param) => {
      inputParameters = {
        ...inputParameters,
        [param.parameter.name]: param.value,
      };
    });

    return {
      id: recommendation.id,
      interval: recommendation.interval,
      carbon: recommendation.carbon,
      metallization: recommendation.metallization,
      productionRate: recommendation.production_rate,
      time: new Date(recommendation.created_at),
      targetParam: RecommendationParamEnum[recommendation.target],
      targetValue: recommendation.target_value,
      parameters: recommendationOutputsTransformer(recommendation.output),
      inputParameters: inputParameters as Record<HMIKeyType, number>,
    };
  });
};
